import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import apiClient from "../../../../../services/apiClient";
import { setSection, updateSection } from "../../../assignmentSlice";
import { CorrectedText } from "../../readers_book/PageFieldLayer";
import "./ChapterBookPage.css";

// 챕터북(지인에듀 원서 모드의 한 갈래, 2026-10-19). 챕터 하나가 섹션 하나이고, 영상 → 책 읽기 → 문제 순서로 지난다.
// question 모양은 api/assignment/chapter_book.py 가 만든다.
//
// - 영상을 끝까지 봐야 다음 단계가 열린다. 앞으로 감기는 이미 본 곳까지만 된다. 다 보면 그 챕터는 학습 완료다
//   (is_finished). 문제를 매기면 is_finished 를 한 번 더 보내 과제 점수를 다시 계산하게 한다.
// - 책 읽기는 음원 시간(장면 start_ms · end_ms, 문장 start_ms · end_ms)에 맞춰 장면을 넘기고 지금 읽는 문장만 진하게 한다.
//   형광펜(배경색)은 쓰지 않는다(직독직해와 같은 규칙).
// - 진도는 section.answer 맨 앞 줄(chapter_book_state)에, 문제 결과는 그 뒤에 문항마다 한 줄씩 남긴다.
//   calculate_grade 는 isCorrect 가 있는 줄만 센다. 쓰기 문제는 원서와 같은 AI 채점(/api/v1/book/grade)으로 매기고
//   ○ 1 · △ 0.5 를 score 로 남긴다(calculate_grade 가 score 를 먼저 본다).

const STATE_TYPE = "chapter_book_state";
const QUESTION_TYPE = "chapter_book_question";
const STEP_VIDEO = "video";
const STEP_READ = "read";
const STEP_QUIZ = "quiz";
// 영상 진도를 이만큼마다 저장한다(나갔다 와도 본 곳부터)
const VIDEO_SAVE_INTERVAL_SECONDS = 15;
// 앞으로 감기를 막을 때 봐주는 여유(초)
const SEEK_SLACK_SECONDS = 1;
const NARROW_SCREEN_QUERY = "(max-width: 760px)";
// 아이 생각을 쓰는 문항. 모범 답은 예시라 AI 가 첨삭만 하고 점수에서는 뺀다(리더스북 자유 쓰기와 같다).
const FREE_WRITING_TYPE_LIST = ["writing"];
// 지인에듀가 label 을 type 그대로 줄 때 보일 이름
const QUESTION_LABEL_BY_TYPE = { events: "사건 순서", best_title: "제목 고르기", main_idea: "주제 · 제목", true_false: "T / F / NG" };

// 챕터가 하나면 지인에듀가 챕터 이름을 'Chapter 1' 로만 줘서 책 제목을 쓴다(서버 chapter_title_of 와 같다)
const chapterHeadingOf = (chapter) =>
  chapter.chapter_count === 1 ? chapter.book_title || chapter.title : chapter.title || chapter.book_title;

const questionLabelOf = (question) =>
  question.label && question.label !== question.type ? question.label : QUESTION_LABEL_BY_TYPE[question.type] || question.label || "";

const readSavedState = (answer) => {
  const list = Array.isArray(answer) ? answer : [];
  const state = list.find((one) => one && one.type === STATE_TYPE) || {};
  const resultList = list.filter((one) => one && one.type === QUESTION_TYPE);
  return { state, resultList };
};

const seededShuffle = (list, seedText) => {
  let seed = 0;
  for (const character of seedText) seed = (seed * 31 + character.charCodeAt(0)) >>> 0;
  const shuffled = [...list];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    seed = (seed * 1103515245 + 12345) >>> 0;
    const pick = seed % (index + 1);
    [shuffled[index], shuffled[pick]] = [shuffled[pick], shuffled[index]];
  }
  return shuffled;
};

// 문제 꼴: 순서 맞추기(답이 목록) · 고르기(보기 있음) · 쓰기
const questionKindOf = (question) => {
  if (Array.isArray(question.answer)) return "order";
  if ((question.choices || []).length > 0) return "choice";
  return "write";
};

const verdictToResult = (verdict) => (verdict === "ok" ? true : verdict === "half" ? "half" : false);

const normalizeText = (text) => String(text || "").toLowerCase().replace(/[^a-z0-9가-힣]/g, "");

const gradeQuestion = (question, input) => {
  const kind = questionKindOf(question);
  if (kind === "order") {
    const answerList = question.answer.map(String);
    return Array.isArray(input) && input.length === answerList.length && input.every((one, index) => one === answerList[index]);
  }
  if (kind === "choice") {
    if (typeof question.answer_index === "number") return input === question.answer_index;
    return input !== undefined && normalizeText(question.choices[input]) === normalizeText(question.answer);
  }
  // 쓰기는 모범 답과 글자가 같으면 바로 맞다고 하고, 다르면 AI 에게 묻는다(null)
  return normalizeText(input) && normalizeText(input) === normalizeText(question.answer) ? true : null;
};

const useNarrowScreen = () => {
  const [isNarrow, setIsNarrow] = useState(() => window.matchMedia(NARROW_SCREEN_QUERY).matches);
  useEffect(() => {
    const media = window.matchMedia(NARROW_SCREEN_QUERY);
    const handleChange = () => setIsNarrow(media.matches);
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);
  return isNarrow;
};

const formatTime = (seconds) => {
  const whole = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
};

const ChapterBookPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);
  const chapter = useMemo(() => section?.question || {}, [section?.question]);
  const scenes = useMemo(() => chapter.scenes || [], [chapter.scenes]);
  const sentences = useMemo(() => chapter.sentences || [], [chapter.sentences]);
  const questions = useMemo(() => chapter.questions || [], [chapter.questions]);
  const hasVideo = Boolean(chapter.video_url);
  const isNarrow = useNarrowScreen();

  const [step, setStep] = useState(STEP_VIDEO);
  const [isVideoDone, setIsVideoDone] = useState(false);
  const [maxWatchedSeconds, setMaxWatchedSeconds] = useState(0);
  const [sceneIndex, setSceneIndex] = useState(0);
  const [inputs, setInputs] = useState({});
  const [results, setResults] = useState(null);
  // 쓰기 문제의 AI 첨삭 {문항 순번: {verdict, score, segments, reason}}
  const [corrections, setCorrections] = useState({});
  const [isGrading, setIsGrading] = useState(false);
  const savedRef = useRef({ state: {}, resultList: [] });

  // section 이 아니라 section.id 에 건다. 저장할 때마다 section 이 통째로 바뀌어 다시 돌면 보던 자리에서 튄다.
  useEffect(() => {
    const saved = isPreview ? { state: {}, resultList: [] } : readSavedState(section?.answer);
    savedRef.current = saved;
    const videoDone = Boolean(saved.state.video_done) || !hasVideo;
    setIsVideoDone(videoDone);
    setMaxWatchedSeconds(Number(saved.state.video_watched_seconds) || 0);
    setSceneIndex(Math.min(Number(saved.state.scene_index) || 0, Math.max(scenes.length - 1, 0)));
    if (saved.resultList.length > 0) {
      setInputs(Object.fromEntries(saved.resultList.map((one) => [one.index, one.input])));
      setResults(Object.fromEntries(saved.resultList.map((one) => [one.index, one.correction ? verdictToResult(one.correction.verdict) : one.isCorrect ?? null])));
      setCorrections(Object.fromEntries(saved.resultList.filter((one) => one.correction).map((one) => [one.index, one.correction])));
    } else {
      setInputs({});
      setResults(null);
      setCorrections({});
    }
    setStep(isReview && saved.resultList.length > 0 ? STEP_QUIZ : videoDone ? STEP_READ : STEP_VIDEO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section?.id]);

  const save = (statePatch, { resultList, isFinished } = {}) => {
    if (isPreview || isReview) return;
    const nextState = { ...savedRef.current.state, ...statePatch, type: STATE_TYPE };
    const nextResultList = resultList || savedRef.current.resultList;
    savedRef.current = { state: nextState, resultList: nextResultList };
    const answer = [nextState, ...nextResultList];
    dispatch(setSection({ ...section, answer }));
    dispatch(updateSection({ sectionId: section.id, data: isFinished ? { answer, is_finished: true } : { answer } }));
  };

  const canLeaveVideo = isVideoDone || isPreview || isReview;
  const stepList = [
    ...(hasVideo ? [{ key: STEP_VIDEO, label: "영상", isOpen: true }] : []),
    { key: STEP_READ, label: "책 읽기", isOpen: canLeaveVideo },
    ...(questions.length > 0 ? [{ key: STEP_QUIZ, label: "문제", isOpen: canLeaveVideo }] : []),
  ];
  const doneStepSet = new Set([
    ...(isVideoDone && hasVideo ? [STEP_VIDEO] : []),
    ...(results ? [STEP_QUIZ] : []),
  ]);

  return (
    <div className="chapter-book">
      <div className="chapter-book__steps">
        {stepList.map((one, index) => (
          <React.Fragment key={one.key}>
            {index > 0 && <span className="chapter-book__step-gap" />}
            <button
              type="button"
              className={`chapter-book__step${step === one.key ? " is-now" : ""}${doneStepSet.has(one.key) ? " is-done" : ""}`}
              disabled={!one.isOpen}
              onClick={() => setStep(one.key)}
            >
              <b>{doneStepSet.has(one.key) ? "✓" : index + 1}</b>
              <span>{one.label}</span>
            </button>
          </React.Fragment>
        ))}
      </div>

      {step === STEP_VIDEO && (
        <VideoStep
          chapter={chapter}
          canLeave={canLeaveVideo}
          isFreeSeek={isPreview || isReview || isVideoDone}
          initialSeconds={maxWatchedSeconds}
          onProgress={(seconds) => {
            setMaxWatchedSeconds(seconds);
            save({ video_watched_seconds: Math.floor(seconds) });
          }}
          onEnded={() => {
            if (!isVideoDone) {
              setIsVideoDone(true);
              save({ video_done: true }, { isFinished: true });
            }
          }}
          onNext={() => setStep(STEP_READ)}
        />
      )}

      {step === STEP_READ && (
        <ReadStep
          chapter={chapter}
          scenes={scenes}
          sentences={sentences}
          sceneIndex={sceneIndex}
          isNarrow={isNarrow}
          onSceneChange={(nextIndex) => {
            setSceneIndex(nextIndex);
            save({ scene_index: nextIndex });
          }}
          onNext={() => (questions.length > 0 ? setStep(STEP_QUIZ) : setCurrentStep(2))}
          onReachEnd={() => {
            // 영상도 문제도 없는 챕터는 끝까지 읽으면 완료다
            if (!hasVideo && questions.length === 0) save({ read_done: true }, { isFinished: true });
          }}
        />
      )}

      {step === STEP_QUIZ && (
        <QuizStep
          chapter={chapter}
          questions={questions}
          seedText={`chapter_book:${section?.id || 0}`}
          inputs={inputs}
          results={results}
          isLocked={Boolean(results) || isReview}
          isNarrow={isNarrow}
          onInput={(index, value) => setInputs({ ...inputs, [index]: value })}
          isGrading={isGrading}
          corrections={corrections}
          onSubmit={async () => {
            const nextResults = Object.fromEntries(
              questions.map((question, index) => [index, gradeQuestion(question, inputs[index])])
            );
            const aiItemList = questions
              .map((question, index) => ({ question, index }))
              .filter(({ question, index }) => questionKindOf(question) === "write" && nextResults[index] === null && String(inputs[index] || "").trim() && question.answer)
              .map(({ question, index }) => ({
                id: String(index),
                prompt: question.question || question.instruction || "",
                quote: question.quote || "",
                explanation: question.explanation_ko || "",
                answer_parts: [String(question.answer)],
                text: String(inputs[index] || "").trim(),
                free_writing: FREE_WRITING_TYPE_LIST.includes(question.type),
              }));
            const nextCorrections = {};
            if (aiItemList.length > 0 && !isPreview) {
              setIsGrading(true);
              try {
                const response = await apiClient.post("/api/v1/book/grade", { section_id: section.id, items: aiItemList });
                Object.entries(response.data.results || {}).forEach(([id, result]) => {
                  if (!result || result.is_pending) return;
                  nextCorrections[id] = { verdict: result.verdict, score: result.score, segments: result.segments || [], reason: result.reason || "" };
                  nextResults[id] = verdictToResult(result.verdict);
                });
              } catch (error) {
                // 채점이 안 되면 그 문항은 점수에서 빼고 모범 답만 보여 준다
              }
              setIsGrading(false);
            }
            setResults(nextResults);
            setCorrections(nextCorrections);
            const resultList = questions.map((question, index) => {
              const correction = nextCorrections[index];
              return {
                type: QUESTION_TYPE,
                index,
                kind: questionKindOf(question),
                input: inputs[index] ?? null,
                ...(correction
                  ? FREE_WRITING_TYPE_LIST.includes(question.type) ? { correction } : { score: correction.score, correction }
                  : nextResults[index] === null ? {} : { isCorrect: nextResults[index] }),
              };
            });
            save({ quiz_done: true }, { resultList, isFinished: isVideoDone || !hasVideo });
          }}
          onFinish={() => setCurrentStep(2)}
        />
      )}
    </div>
  );
};

const VideoStep = ({ chapter, canLeave, isFreeSeek, initialSeconds, onProgress, onEnded, onNext }) => {
  const videoRef = useRef(null);
  const maxWatchedRef = useRef(initialSeconds || 0);
  const lastSavedRef = useRef(initialSeconds || 0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSeconds, setCurrentSeconds] = useState(0);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [maxWatched, setMaxWatched] = useState(initialSeconds || 0);

  // 다른 탭으로 가면 멈춘다. 틀어 두고 딴 데 가 있는 것을 본 것으로 치지 않는다.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && videoRef.current) videoRef.current.pause();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const handleLoaded = () => {
    const video = videoRef.current;
    setDurationSeconds(video.duration || 0);
    // 나갔다 오면 본 곳부터 이어 본다
    if (maxWatchedRef.current > 0 && maxWatchedRef.current < (video.duration || 0) - 1) {
      video.currentTime = maxWatchedRef.current;
    }
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    setCurrentSeconds(video.currentTime);
    if (video.currentTime > maxWatchedRef.current && video.currentTime <= maxWatchedRef.current + SEEK_SLACK_SECONDS + 1) {
      maxWatchedRef.current = video.currentTime;
      setMaxWatched(video.currentTime);
      if (video.currentTime - lastSavedRef.current >= VIDEO_SAVE_INTERVAL_SECONDS) {
        lastSavedRef.current = video.currentTime;
        onProgress(video.currentTime);
      }
    }
  };

  // 앞으로 감기는 이미 본 곳까지만 된다
  const handleSeeking = () => {
    const video = videoRef.current;
    if (!isFreeSeek && video.currentTime > maxWatchedRef.current + SEEK_SLACK_SECONDS) {
      video.currentTime = maxWatchedRef.current;
    }
  };

  const handleBarClick = (event) => {
    const video = videoRef.current;
    if (!video || !durationSeconds) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const wanted = ((event.clientX - rect.left) / rect.width) * durationSeconds;
    video.currentTime = isFreeSeek ? wanted : Math.min(wanted, maxWatchedRef.current);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    // 재생이 막혀도(자동 재생 정책 · 못 여는 영상) 화면이 죽지 않게 한다
    if (video.paused) video.play().catch(() => setIsPlaying(false));
    else video.pause();
  };

  const playedShare = durationSeconds ? (currentSeconds / durationSeconds) * 100 : 0;
  const watchedShare = durationSeconds ? (Math.max(maxWatched, currentSeconds) / durationSeconds) * 100 : 0;

  return (
    <div className="chapter-book__video-step">
      <div className="chapter-book__chapter-title">{chapterHeadingOf(chapter)}</div>
      <div className="chapter-book__video-frame">
        <video
          ref={videoRef}
          src={chapter.video_url}
          playsInline
          preload="metadata"
          onClick={togglePlay}
          onLoadedMetadata={handleLoaded}
          onTimeUpdate={handleTimeUpdate}
          onSeeking={handleSeeking}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => {
            setIsPlaying(false);
            maxWatchedRef.current = durationSeconds;
            setMaxWatched(durationSeconds);
            onEnded();
          }}
        />
      </div>
      <div className="chapter-book__video-controls">
        <button type="button" className="chapter-book__round" onClick={togglePlay} aria-label={isPlaying ? "멈춤" : "재생"}>
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>
        <div className="chapter-book__bar" onClick={handleBarClick}>
          <div className="chapter-book__bar-watched" style={{ width: `${watchedShare}%` }} />
          <div className="chapter-book__bar-played" style={{ width: `${playedShare}%` }} />
          <div className="chapter-book__bar-knob" style={{ left: `${playedShare}%` }} />
        </div>
        <span className="chapter-book__time">{formatTime(currentSeconds)} / {formatTime(durationSeconds)}</span>
        <button type="button" className={`chapter-book__next${canLeave ? " is-open" : ""}`} disabled={!canLeave} onClick={onNext}>
          {!canLeave && <LockIcon />}책 읽기
        </button>
      </div>
    </div>
  );
};

// 장면 글을 문장으로 나눠 그린다. 문장 시간이 없으면 장면 글을 그대로 쓴다.
const sceneSentenceList = (scene, sentenceByIndex) => {
  const list = (scene.sentence_index_list || []).map((index) => sentenceByIndex[index]).filter(Boolean);
  return list.length > 0 ? list : null;
};

// 장면 글 길이(글자 수)에 따른 글씨 크기. 글밥이 적은 쪽은 크게 보여 준다(2026-10-19 대표).
const PAGE_TEXT_SIZE_LIST = [
  { maximumLength: 60, className: "is-short" },
  { maximumLength: 140, className: "is-medium" },
];
const pageTextSizeClassOf = (text) =>
  (PAGE_TEXT_SIZE_LIST.find((one) => String(text || "").length <= one.maximumLength) || {}).className || "";

const ReadStep = ({ chapter, scenes, sentences, sceneIndex, isNarrow, onSceneChange, onNext, onReachEnd }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAutoTurn, setIsAutoTurn] = useState(true);
  const [readingSentenceIndex, setReadingSentenceIndex] = useState(null);
  const sentenceByIndex = useMemo(() => Object.fromEntries(sentences.map((one) => [one.idx, one])), [sentences]);
  const scene = scenes[sceneIndex] || {};
  const sentenceList = sceneSentenceList(scene, sentenceByIndex);
  const isLastScene = sceneIndex >= scenes.length - 1;

  useEffect(() => {
    if (isLastScene) onReachEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLastScene]);

  // 장면을 손으로 넘기면 음원도 그 장면 처음으로 옮긴다
  const goScene = (nextIndex) => {
    const bounded = Math.min(Math.max(nextIndex, 0), Math.max(scenes.length - 1, 0));
    onSceneChange(bounded);
    const audio = audioRef.current;
    const startMs = scenes[bounded]?.start_ms;
    if (audio && typeof startMs === "number") audio.currentTime = startMs / 1000;
  };

  const handleTimeUpdate = () => {
    const nowMs = audioRef.current.currentTime * 1000;
    const reading = sentences.find((one) => one.start_ms !== null && one.start_ms <= nowMs && nowMs < one.end_ms);
    setReadingSentenceIndex(reading ? reading.idx : null);
    if (!isAutoTurn) return;
    const playingScene = scenes.findIndex((one) => one.start_ms !== null && one.start_ms <= nowMs && nowMs < one.end_ms);
    if (playingScene >= 0 && playingScene !== sceneIndex) onSceneChange(playingScene);
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      const startMs = scene.start_ms;
      const nowMs = audio.currentTime * 1000;
      // 지금 보는 장면 밖에 있으면 그 장면 처음부터 읽는다
      if (typeof startMs === "number" && (nowMs < startMs || (scene.end_ms && nowMs >= scene.end_ms))) {
        audio.currentTime = startMs / 1000;
      }
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  };

  return (
    <div className="chapter-book__read-step">
      {chapter.audio_url && (
        <audio
          ref={audioRef}
          src={chapter.audio_url}
          preload="auto"
          onTimeUpdate={handleTimeUpdate}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
        />
      )}
      <div className={`chapter-book__spread${isNarrow ? " is-narrow" : ""}`}>
        <div className="chapter-book__picture">
          {scene.picture_url ? <img src={scene.picture_url} alt="" /> : null}
        </div>
        <div className={`chapter-book__page-text ${pageTextSizeClassOf(scene.text)}`}>
          <p>
            {sentenceList
              ? sentenceList.map((sentence) => (
                  <span key={sentence.idx} className={sentence.idx === readingSentenceIndex ? "is-reading" : ""}>
                    {sentence.text}{" "}
                  </span>
                ))
              : scene.text}
          </p>
        </div>
      </div>
      <div className="chapter-book__book-controls">
        <button type="button" className="chapter-book__flip" onClick={() => goScene(sceneIndex - 1)} disabled={sceneIndex === 0} aria-label="앞 장면">‹</button>
        {chapter.audio_url && (
          <button type="button" className="chapter-book__play" onClick={togglePlay} aria-label={isPlaying ? "멈춤" : "듣기"}>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
        )}
        {isLastScene ? (
          <button type="button" className="chapter-book__next is-open" onClick={onNext}>다음</button>
        ) : (
          <button type="button" className="chapter-book__flip" onClick={() => goScene(sceneIndex + 1)} aria-label="다음 장면">›</button>
        )}
        {!isNarrow && (
          <span className="chapter-book__page-count">{sceneIndex + 1} / {scenes.length}</span>
        )}
        {chapter.audio_url && (
          <label className="chapter-book__auto">
            <input type="checkbox" checked={isAutoTurn} onChange={(event) => setIsAutoTurn(event.target.checked)} />
            자동 넘김
          </label>
        )}
      </div>
    </div>
  );
};

const QuizStep = ({ chapter, questions, seedText, inputs, results, corrections, isLocked, isGrading, isNarrow, onInput, onSubmit, onFinish }) => {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const paragraphList = String(chapter.text || "").split(/\n\s*\n/).filter((one) => one.trim());
  const quote = (questions[focusedIndex] || {}).quote || "";
  const orderChoiceListByIndex = useMemo(
    () => Object.fromEntries(
      questions.map((question, index) => [
        index,
        questionKindOf(question) === "order" ? seededShuffle(question.answer.map(String), `${seedText}:${index}`) : [],
      ])
    ),
    [questions, seedText]
  );

  const renderParagraph = (paragraph, paragraphIndex) => {
    if (!quote || !paragraph.includes(quote)) return <p key={paragraphIndex}>{paragraph}</p>;
    const [before, ...rest] = paragraph.split(quote);
    return (
      <p key={paragraphIndex}>
        {before}<span className="chapter-book__quote">{quote}</span>{rest.join(quote)}
      </p>
    );
  };

  return (
    <div className={`chapter-book__quiz${isNarrow ? " is-narrow" : ""}`}>
      <div className="chapter-book__story">
        <h4>{chapterHeadingOf(chapter)}</h4>
        {paragraphList.map(renderParagraph)}
      </div>
      <div className="chapter-book__questions">
        {questions.map((question, index) => {
          const kind = questionKindOf(question);
          const result = results ? results[index] : undefined;
          const resultClass = result === true ? " is-right" : result === false ? " is-wrong" : result === "half" ? " is-half" : "";
          const correction = corrections[index];
          return (
            <div key={index} className={`chapter-book__question${resultClass}`} onClick={() => setFocusedIndex(index)}>
              <span className="chapter-book__tag">{questionLabelOf(question)}</span>
              {question.instruction && <div className="chapter-book__instruction">{question.instruction}</div>}
              {question.question && <div className="chapter-book__stem">{index + 1}. {question.question}</div>}
              {kind === "choice" && (
                <div className={`chapter-book__choices${question.choices.length <= 3 && question.choices.every((one) => one.length <= 3) ? " is-row" : ""}`}>
                  {question.choices.map((choice, choiceIndex) => (
                    <button
                      type="button"
                      key={choiceIndex}
                      disabled={isLocked}
                      className={`chapter-book__choice${inputs[index] === choiceIndex ? " is-picked" : ""}`}
                      onClick={() => onInput(index, choiceIndex)}
                    >
                      {choice}
                    </button>
                  ))}
                </div>
              )}
              {kind === "order" && (
                <OrderQuestion
                  choiceList={orderChoiceListByIndex[index]}
                  value={Array.isArray(inputs[index]) ? inputs[index] : []}
                  isLocked={isLocked}
                  onChange={(next) => onInput(index, next)}
                />
              )}
              {kind === "write" && !correction && (
                <textarea
                  className="chapter-book__write"
                  rows={FREE_WRITING_TYPE_LIST.includes(question.type) ? 5 : 2}
                  disabled={isLocked}
                  value={inputs[index] || ""}
                  onChange={(event) => onInput(index, event.target.value)}
                />
              )}
              {kind === "write" && correction && (
                <div className="chapter-book__corrected">
                  <CorrectedText segmentList={correction.segments} fallbackText={inputs[index] || ""} />
                  {correction.reason && <div className="chapter-book__reason">{correction.reason}</div>}
                </div>
              )}
              {results && result !== true && !correction && (
                <div className="chapter-book__answer">
                  {kind === "order" ? question.answer.join(" → ") : kind === "choice" && typeof question.answer_index === "number" ? question.choices[question.answer_index] : String(question.answer || "")}
                  {question.explanation_ko && <div className="chapter-book__explanation">{question.explanation_ko}</div>}
                </div>
              )}
            </div>
          );
        })}
        {results ? (
          <button type="button" className="chapter-book__submit" onClick={onFinish}>끝내기</button>
        ) : (
          <button type="button" className="chapter-book__submit" onClick={onSubmit} disabled={isLocked || isGrading}>
            {isGrading ? "채점하는 중…" : "채점하기"}
          </button>
        )}
      </div>
    </div>
  );
};

// 사건 순서: 섞인 문장을 차례로 누른다. 누른 것을 다시 누르면 뺀다.
const OrderQuestion = ({ choiceList, value, isLocked, onChange }) => (
  <div className="chapter-book__order">
    {choiceList.map((choice) => {
      const position = value.indexOf(choice);
      return (
        <button
          type="button"
          key={choice}
          disabled={isLocked}
          className={`chapter-book__order-item${position >= 0 ? " is-picked" : ""}`}
          onClick={() => onChange(position >= 0 ? value.filter((one) => one !== choice) : [...value, choice])}
        >
          <b>{position >= 0 ? position + 1 : ""}</b>
          {choice}
        </button>
      );
    })}
  </div>
);

const PlayIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M4 2.5v11l9-5.5z" fill="currentColor" /></svg>
);
const PauseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><rect x="3" y="2" width="3.5" height="12" rx="1" fill="currentColor" /><rect x="9.5" y="2" width="3.5" height="12" rx="1" fill="currentColor" /></svg>
);
const LockIcon = () => (
  <svg width="12" height="13" viewBox="0 0 12 13" aria-hidden="true"><rect x="1" y="5.5" width="10" height="7" rx="1.5" fill="currentColor" /><path d="M3.5 5.5V4a2.5 2.5 0 0 1 5 0v1.5" stroke="currentColor" strokeWidth="1.6" fill="none" /></svg>
);

export default ChapterBookPage;
